<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import FeatureIcon from '../FeatureIcon.vue';
import { useSiteForm } from '../useSiteForm.js';
import { quickLinkItems, quickLinksSubscribe } from '../quickLinks.js';
import { styleVars } from './shared.js';
import EmailTypoHint from '@/Components/EmailTypoHint.vue';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// No links and no subscribe box is no block for a visitor, heading
// included; the editor keeps its prompt (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const links = computed(() => quickLinkItems(props.config));
const subscribe = computed(() => quickLinksSubscribe(props.config));

// The subscribe tile is the newsletter form, so a signup here lands
// in the same subscriber list as the Newsletter block's.
const { values, errors, status, message, submit, turnstileKey, mountTurnstile } = useSiteForm('newsletter', {
    siteData: props.site_data,
    fields: { email: '' },
});
const turnstileEl = ref(null);
onMounted(() => { if (turnstileKey && subscribe.value) mountTurnstile(turnstileEl.value); });
</script>

<template>
    <section v-if="links.length || subscribe || editing" :id="config.anchor_id || null" class="th-block th-sec th-ql" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.eyebrow || config.heading" class="th-head th-ql__head">
                <div>
                    <p v-if="config.eyebrow" class="th-eyebrow">{{ config.eyebrow }}</p>
                    <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                </div>
            </div>

            <div v-if="links.length || subscribe" class="th-ql__grid">
                <a v-for="(l, i) in links" :key="i" :href="l.href || '#'" class="th-ql__tile">
                    <span class="th-ql__icon"><FeatureIcon :name="l.icon" /></span>
                    <span class="th-ql__title-row">
                        <h3 class="th-ql__title">{{ l.title }}</h3>
                        <span class="th-ql__arrow" aria-hidden="true">→</span>
                    </span>
                    <p v-if="l.text" class="th-ql__text">{{ l.text }}</p>
                </a>

                <div v-if="subscribe" class="th-ql__tile th-ql__tile--subscribe">
                    <span class="th-ql__icon"><FeatureIcon name="mail" /></span>
                    <h3 class="th-ql__title">{{ subscribe.title }}</h3>
                    <p v-if="subscribe.text" class="th-ql__text">{{ subscribe.text }}</p>
                    <p v-if="status === 'sent'" class="th-ql__sent" role="status">{{ subscribe.success_message || message }}</p>
                    <form v-else class="th-ql__form" novalidate @submit.prevent="submit()">
                        <div class="th-ql__row">
                            <input v-model="values.email" type="email" name="email" autocomplete="email"
                                   class="th-ql__email"
                                   :placeholder="subscribe.placeholder"
                                   :aria-label="subscribe.placeholder">
                            <input v-model="values.website" class="th-hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
                            <button type="submit" class="th-ql__go" :disabled="status === 'sending'">
                                {{ status === 'sending' ? '…' : subscribe.button_label }}
                            </button>
                        </div>
                        <EmailTypoHint :email="values.email" @fix="values.email = $event" />
                        <span v-if="errors.email" class="th-ql__error">{{ errors.email }}</span>
                        <span v-else-if="status === 'error' && message" class="th-ql__error" role="alert">{{ message }}</span>
                        <div v-if="turnstileKey" ref="turnstileEl"></div>
                    </form>
                </div>
            </div>
            <p v-else-if="editing" class="th-small th-ql__empty">Add links in the editor.</p>
        </div>
    </section>
</template>

<style scoped>
.th-ql { background: var(--block-bg, transparent); }
.th-ql__head { margin-bottom: 24px; }
/* 1px gaps over a hairline ring read as one ruled panel, the way the
   design draws it, and wrap to two or three a row on smaller screens. */
.th-ql__grid { display: flex; flex-wrap: wrap; gap: 1px; }
.th-ql__tile {
    flex: 1 1 150px;
    display: flex;
    flex-direction: column;
    gap: clamp(8px, 1.2vw, 12px);
    padding: clamp(16px, 2.5vw, 24px) clamp(14px, 2vw, 22px);
    background: #fff;
    box-shadow: 0 0 0 1px var(--th-line);
    color: var(--block-text, var(--th-ink));
    text-decoration: none !important;
    transition: background .15s ease;
}
a.th-ql__tile:hover, a.th-ql__tile:focus-visible { background: var(--tint); }
.th-ql__icon { width: 26px; height: 26px; color: var(--accent); }
.th-ql__title-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.th-ql__title {
    font: 800 clamp(18px, 2vw, 22px)/1.1 var(--th-display);
    font-stretch: 108%;
    margin: 0;
    color: var(--text-heading, inherit);
}
.th-ql__arrow { color: var(--accent); font-weight: 700; }
.th-ql__text {
    margin: 0;
    font-size: clamp(13px, 1.3vw, 15px);
    line-height: 1.45;
    color: var(--text-sub, var(--th-muted));
    text-wrap: pretty;
}

.th-ql__tile--subscribe { flex: 1 1 300px; background: var(--accent); color: #fff; gap: 12px; }
.th-ql__tile--subscribe .th-ql__icon,
.th-ql__tile--subscribe .th-ql__title,
.th-ql__tile--subscribe .th-ql__text { color: #fff; }
.th-ql__tile--subscribe .th-ql__text { font-size: 15px; }
.th-ql__form { margin-top: auto; display: flex; flex-direction: column; gap: 6px; }
.th-ql__row { display: flex; gap: 6px; }
.th-ql__email {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    border: 0;
    border-radius: var(--r);
    font-size: 15px;
    color: #1b1d1a;
    background: #fff;
}
.th-ql__go {
    border: 0;
    background: #1b1d1a;
    color: #fff;
    padding: 10px 14px;
    border-radius: var(--r);
    font-weight: 600;
    font-size: 15px;
    cursor: pointer;
}
.th-ql__go:disabled { opacity: .7; cursor: default; }
.th-ql__error { font-size: 13px; color: #fff; font-weight: 600; }
.th-ql__sent { margin: auto 0 0; font-weight: 600; }
.th-ql__empty { font-style: italic; }
</style>
